import { HttpErrorResponse } from '@angular/common/http';
import type { OnInit, WritableSignal } from '@angular/core';
import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AppErrorHandler } from '@shared/services/error-handler/app-error-handler.service';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import type { LoginCredentials } from '../models/login-credentials';
import type { LoginResponse } from '../models/login-response';
import { LoginAttemptResult } from '../models/login-response';
import { AuthService } from '../services/auth.service';
import { SnackBarService } from '@carelinelive/ui';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import type { ResultObservable } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { loading, Result, ResultState } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { Country } from '@shared/country';
import type { SingleSignOnConnectionState } from '../sso.service';
import { SingleSignOnService } from '../sso.service';
import { Feature, isFeatureEnabled } from '@shared/features';
import { ActivatedRoute, Router } from '@angular/router';

enum Step {
    Email,
    Credentials,
    MfaChallenge,
    Success,
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
    protected readonly Step = Step;

    protected readonly Country = Country;

    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _auth = inject(AuthService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _errorHandler = inject(AppErrorHandler);
    private readonly _router = inject(Router);
    private readonly _snackBar = inject(SnackBarService);
    protected readonly sso = inject(SingleSignOnService);

    protected readonly currentStep: WritableSignal<Step> = signal(Step.Email);

    showStartUpMessage = true;

    protected readonly form: LoginForm = new FormGroup({
        email: new FormControl<string>(null, {
            validators: [Validators.email, Validators.required],
        }),
        password: new FormControl<string>(null, [Validators.required]),
        mfa_code: new FormControl<string>({ value: null, disabled: true }, [Validators.required]),
        remember: new FormControl<boolean>(false),
    });

    protected readonly email$ = new BehaviorSubject<string>('');

    protected successfulLoginResponse: LoginResponse = null;
    protected requestingOtp: boolean = false;

    /** Whether the user has been redirected to an SSO authorisation page */
    protected readonly ssoLaunched = signal(false);

    protected readonly ssoClientsRequest$: ResultObservable<SingleSignOnConnectionState> = this.email$.pipe(
        map(email => email?.trim()),
        switchMap(email => {
            if (!email) {
                return of(Result.init<SingleSignOnConnectionState>());
            }

            return this.sso.getConnections(email);
        }),
        shareReplay(1),
    );

    protected ssoStateResult = toSignal(this.ssoClientsRequest$);

    protected showPasswordInput = computed(() => {
        if (!this.ssoEnabled) {
            return true;
        }

        const state = this.ssoStateResult();

        if (state.state === ResultState.Success) {
            return state.value.allow_password;
        }

        return false;
    });

    protected readonly loginPayload$ = new Subject<LoginCredentials>();
    protected readonly loginRequest$: ResultObservable<LoginResponse> = this.loginPayload$.pipe(
        switchMap(credentials => {
            return this._auth.login(credentials);
        }),

        map(result => {
            const errorResponse = result.error;

            if (errorResponse instanceof HttpErrorResponse) {
                const error: LoginResponse = errorResponse.error?.data;

                if (error) {
                    const status = error.status;

                    if (status !== LoginAttemptResult.success && status !== LoginAttemptResult.mfaChallenge) {
                        this._snackBar.openError(error.message, {
                            testId: 'snackbar:login-error',
                        });

                        if (status === LoginAttemptResult.invalidCredentials) {
                            this.form.controls.email.setErrors({
                                customError: 'Invalid email or password',
                            });
                        }
                    }

                    if (status === LoginAttemptResult.mfaChallenge) {
                        this.form.controls.mfa_code.enable();
                        this.currentStep.set(Step.MfaChallenge);
                    }

                    return Result.withValue(error);
                }
            }

            return result;
        }),
        shareReplay(1),
    );

    protected readonly isLoggingIn = toSignal(this.loginRequest$.pipe(loading(), shareReplay(1)));
    protected readonly redirectErrorMessage = signal<{
        status: LoginAttemptResult;
        message: string;
    } | undefined>(undefined);

    public showRememberMe: boolean = window.CareLineLive.remember?.enabled ?? true;

    protected readonly ssoEnabled = isFeatureEnabled(Feature.SingleSignOn);

    constructor() {
        effect(() => {
            const currentStep = this.currentStep();

            if (currentStep === Step.MfaChallenge) {
                this.form.controls.mfa_code.enable();
            } else {
                this.form.controls.mfa_code.disable();
            }

            if (currentStep === Step.Email) {
                this.form.controls.email.enable();
            } else {
                this.form.controls.email.disable();
            }
        }, {
            allowSignalWrites: true,
        });

        this.form.controls.password.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
            this.form.controls.email.setErrors(null);
        });

        this.loginRequest$
            .pipe(
                tap(request => console.log(request)),
                takeUntilDestroyed(),
                filter(response => response.isSuccess()),
                map(response => response.value),
                filter(response => response.status === LoginAttemptResult.success),
            )
            .subscribe(response => {
                if (response.status === LoginAttemptResult.success) {
                    this.currentStep.set(Step.Success);
                    this.successfulLoginResponse = response;
                } else {
                    this.successfulLoginResponse = null;
                }
            });
    }

    ngOnInit(): void {
        const queryParams = this._activatedRoute.snapshot.queryParams;

        if (queryParams.message) {
            this.redirectErrorMessage.set({
                status: queryParams.status,
                message: queryParams.message,
            });

            this._router.navigate([], {
                relativeTo: this._activatedRoute,
                queryParams: {
                    message: null,
                    status: null,
                },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        }
    }


    protected continue($event: MouseEvent | SubmitEvent): void {
        if ($event instanceof SubmitEvent) {
            $event.preventDefault();
        }

        if (this.currentStep() === Step.Email) {
            this.email$.next(this.form.value.email);

            if (this.ssoEnabled) {
                this.currentStep.set(Step.Credentials);
                return;
            }

            this.loginPayload$.next(this.buildCredentials());

            return;
        }

        if (this.currentStep() === Step.Credentials) {
            this.loginPayload$.next(this.buildCredentials());
        }

        if (this.currentStep() === Step.MfaChallenge) {
            this.loginPayload$.next(this.buildCredentials());
        }
    }

    requestOtp() {
        if (this.requestingOtp) {
            return;
        }

        const progress = this._snackBar.openProgress({
            title: 'Requesting OTP code',
            testId: 'snackbar:requesting-otp',
        });

        this._auth
            .requestOtp(this.buildCredentials())
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: () => {
                    this.requestingOtp = false;

                    progress.dismiss();

                    this._snackBar.openSuccess('Code requested, it may take a couple of minutes to arrive', {
                        testId: 'snackbar:code-requested',
                    });
                },
                error: error => {
                    this.requestingOtp = false;
                    progress.dismiss();
                    this._errorHandler.handle(error);
                },
            });
    }

    private buildCredentials(): LoginCredentials {
        return {
            email: this.email$.value,
            password: this.form.value.password,
            remember: this.form.value.remember,
            mfa_code: this.form.value.mfa_code,
        };
    }

    public backFromMfaChallenge() {
        this.currentStep.set(Step.Email);
        this.form.controls.mfa_code.disable();
    }

    protected clearEmail(): void {
        this.email$.next(null);

        this.currentStep.set(Step.Email);
        this.ssoLaunched.set(false);
    }

    protected onLaunchedSingleSignOn(): void {
        this.ssoLaunched.set(true);
    }
}

type LoginForm = FormGroup<{
    email: FormControl<string>;
    password: FormControl<string>;
    mfa_code: FormControl<string | null>;
    remember: FormControl<boolean | null>;
}>;
